import {
  salesforceImportMappingFormProps,
} from "../constants/ComponentTypes";
import moment from "moment";
import { transform, isEqual, isObject } from 'lodash';
import { departmentNestedMenu } from "../constants/SidebarValues";
const { Set, Map } = require("immutable");

/**
 * レターの注文ステータスに基づいてテキストを生成
 *
 * @param {object} letterOrder 注文オブジェクト
 * @returns {string} 生成されたテキスト
 */
export const letterOrderStatusText = (letterOrder) => {
  let isScheduledAfter = false;
  let formattedOrderScheduled = '';
  let formattedInstructed = '';

  if (letterOrder && letterOrder.order_scheduled_at) {
    // 注文予定日時が過去かどうかを判定
    isScheduledAfter = isOrderScheduledAfter(letterOrder.order_scheduled_at);

    // 注文予定日時を整形
    const orderScheduledAt = new Date(letterOrder.order_scheduled_at);
    // 桁数
    const digits = 2;
    formattedOrderScheduled = `${orderScheduledAt.getFullYear()}年${(orderScheduledAt.getMonth() + 1).toString().padStart(digits, '0')}月${orderScheduledAt.getDate().toString().padStart(digits, '0')}日`;
  }

  if (isScheduledAfter && letterOrder && letterOrder.instructed_at) {
    // 注文指示日時を整形
    const instructedAt = new Date(letterOrder.instructed_at);
    // 桁数
    const digits = 2;
    formattedInstructed = `${instructedAt.getFullYear()}年${(instructedAt.getMonth() + 1).toString().padStart(digits, '0')}月${instructedAt.getDate().toString().padStart(digits, '0')}`;
  }

  let statusText = '';
  if (letterOrder.letter_order_addresses_count === 0) {
    statusText = formattedOrderScheduled + ' レターの注文データを作成中です';
  } else if (letterOrder && letterOrder.order_scheduled_at && isScheduledAfter) {
    // 注文予約の場合は「注文指示日 + レターの注文が予約されました（注文予定日：xx年xx月xx日）」を表示
    statusText = formattedInstructed + ' レターの注文が予約されました（注文予定日：' + formattedOrderScheduled + '）';
  } else {
    statusText = formattedOrderScheduled + ' レターが注文されました';
  }

  return statusText;
};

export const addCommas = (number) => {
  number += "";
  let parts = number.split(".");
  let integer = parts[0];
  let decimal = parts.length > 1 ? "." + parts[1] : "";
  let rgx = /(\d+)(\d{3})/;
  while (rgx.test(integer)) {
    integer = integer.replace(rgx, `$1,$2`);
  }
  return integer + decimal;
};

export const formatName = (contact, honorific = "") => {
  if (contact && contact.name && (contact.company || contact.company_name))
    return `${contact.company || contact.company_name} ${contact.name
      } ${honorific}`;
  else if (contact.name) return contact.name + " " + honorific;
  else return (contact.company || contact.company_name) + " " + honorific;
};

export const formatCompanyWithHonorific = (contact, honorific = "") => {
  if (contact && contact.honorific && (contact.company || contact.company_name))
    return `${contact.company || contact.company_name} ${contact.name
      } ${contact.honorific}`;
  else if (contact && contact.company_name) return contact.company_name + " " + contact.honorific;
  else return (contact.company || contact.company_name) + " " + honorific;
};

export const formatNameWithPosition = (contact) => {
  return `${contact.company ? contact.company + " " : ""}
  ${contact.branch ? contact.branch + " " : ""}
  ${contact.position ? contact.position + " " : ""}
  ${contact.name ? contact.name : ""}`;
};

export const stripPostcode = (postcode) => {
  if (!postcode) return;

  return postcode
    .toString()
    .replace(/[\uff0d\u30fc\uff70\u2013\u2796]/g, "")
    .replace(/[‐－―ー-]/g, "")
    .replace("〒", "")
    .trim();
};

export const formatPostcode = (postcode) => {
  if (!postcode || typeof postcode !== "string") return;

  let stripped = stripPostcode(postcode);

  const postCodeLength = 7;
  if (stripped.length === postCodeLength) {
    const firstCodeLength = 3;
    const secondCodeLength = 4;
    return `〒${stripped.substr(0, firstCodeLength)}-${stripped.substr(firstCodeLength, secondCodeLength)}`;
  } else {
    return "";
  }
};

export const formatNgItemName = (contact) => {
  if (contact && contact.name && contact.corporation && contact.corporation.name)
    return `${contact.corporation.name} ${contact.name}`;
  else if (contact && contact.name) return contact.name;
  else return contact.corporation.name;
};

export const formatProfile = (item) => {
  let date = "",
    honorific = "";
  date = moment(item.relocation_at.date).format("YYYY年MM月") + "に";

  if (item.honorific.indexOf("退任") !== -1) {
    const beforeHonorific = getBeforeHonorificExceptRetirement(item);
    // 経歴がない場合は日付 + 退任。を表示
    if (!beforeHonorific) {
      return date + "退任。";
    }
    honorific = beforeHonorific + "を退任";
  } else if (
    item.honorific.indexOf("入社") !== -1 ||
    item.honorific.indexOf("就任") !== -1 ||
    item.honorific.indexOf("着任") !== -1
  ) {
    honorific = item.honorific;
  } else {
    honorific = item.honorific + "に着任";
  }

  return date + honorific + "。";
};

const ZENKAKU_OFFSET = 0xFEE0;

// 全角・半角変換用の関数
const toZenkaku = (str) => {
  return str.replace(/[A-Za-z0-9!-~]/g, (s) => {
    return String.fromCharCode(s.charCodeAt(0) + ZENKAKU_OFFSET);
  });
};

const toHankaku = (str) => {
  return str.replace(/[Ａ-Ｚａ-ｚ０-９！-～]/g, (s) => {
    return String.fromCharCode(s.charCodeAt(0) - ZENKAKU_OFFSET);
  });
};

// 先頭文字を大文字にする関数
const ucfirst = (str) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

// ひらがな・カタカナの拗音・促音を大文字に変換する関数
const convertKanaSmallToLarge = (text) => {
  const search = ['ぁ','ぃ','ぅ','ぇ','ぉ','っ','ゃ','ゅ','ょ','ゎ','ァ','ィ','ゥ','ェ','ォ','ッ','ャ','ュ','ョ','ヮ'];
  const replace = ['あ','い','う','え','お','つ','や','ゆ','よ','わ','ア','イ','ウ','エ','オ','ツ','ヤ','ユ','ヨ','ワ'];
  return text.split('').map(char => {
    const index = search.indexOf(char);
    return index > -1 ? replace[index] : char;
  }).join('');
};

export const filterSortIntentTags = (item, filterIntentKeywords) => {
  let tags = [];
  if (item) {
    if (item.reaction_tags) {
      tags = tags.concat(item.reaction_tags.trim().split(/\s*,\s*/));
    }

    if (item.invest_business_area_tags) {
      tags = tags.concat(item.invest_business_area_tags.trim().split(/\s*,\s*/));
    }

    if (item.web_tool_tags) {
      tags = tags.concat(item.web_tool_tags.trim().split(/\s*,\s*/));
    }

    if (item.corporation_statistics_change_tags) {
      tags = tags.concat(item.corporation_statistics_change_tags.trim().split(/\s*,\s*/));
    }

    if (item.corporation_intent_tags) {
      // 会社タグを分解
      const corporationIntentTags = item.corporation_intent_tags.trim().split(/\s*,\s*/);
      // キーワードがtagsに含まれるかチェック(バックエンドの検索処理と同等)
      const filteredCorporationIntentTags = corporationIntentTags.filter(tag => {
        return filterIntentKeywords.some(keyword => {
          // 変換処理
          const zen = toZenkaku(keyword);
          const prefixZen = toZenkaku(ucfirst(keyword));
          const han = toHankaku(keyword);
          const prefixHan = toHankaku(ucfirst(keyword));
          const large = convertKanaSmallToLarge(keyword);

          return [zen, prefixZen, han, prefixHan, large].some(convertedKeyword => tag.includes(convertedKeyword));
        });
      });

      tags = tags.concat(filteredCorporationIntentTags);
    }

    // 重複を排除
    tags = [...new Set(tags)];

    // キーワードに一致するタグを先頭に移動
    if (filterIntentKeywords && filterIntentKeywords.length > 0) {
      tags.sort((a, b) => {
        const aMatches = filterIntentKeywords.some(keyword => {
          const zen = toZenkaku(keyword);
          const prefixZen = toZenkaku(ucfirst(keyword));
          const han = toHankaku(keyword);
          const prefixHan = toHankaku(ucfirst(keyword));
          const large = convertKanaSmallToLarge(keyword);

          return [zen, prefixZen, han, prefixHan, large].some(convertedKeyword => a.includes(convertedKeyword));
        });

        const bMatches = filterIntentKeywords.some(keyword => {
          const zen = toZenkaku(keyword);
          const prefixZen = toZenkaku(ucfirst(keyword));
          const han = toHankaku(keyword);
          const prefixHan = toHankaku(ucfirst(keyword));
          const large = convertKanaSmallToLarge(keyword);

          return [zen, prefixZen, han, prefixHan, large].some(convertedKeyword => b.includes(convertedKeyword));
        });

        if (aMatches && !bMatches) return -1;
        if (!aMatches && bMatches) return 1;
        return 0;
      });
    }
  }

  return tags;
}

/**
 * 部門キーをタグに変換する
 * @param {Array} departmentKeys 部門キーの配列
 * @returns {Array} タグの配列
 */
export const convertDepartmentKeysToTags = (departmentKeys) => {
  if (!Array.isArray(departmentKeys) || departmentKeys.length === 0) return [];

  // 値→エントリのマップを作成して素早く名前へ解決
  const valueToEntryMap = createValueToEntryMap(departmentNestedMenu);

  const seen = new Set();
  const tags = [];

  departmentKeys.forEach((key) => {
    if (key == null) return;
    const entry = valueToEntryMap[key];
    if (entry && !seen.has(entry.name)) {
      seen.add(entry.name);
      tags.push(entry.name);
    }
  });

  return tags;
};

export const getBeforeHonorificExceptRetirement = (item) => {
  const executive_biographies = item.executive_biographies;
  if (executive_biographies && executive_biographies.length > 1) {
    for (let i = executive_biographies.length - 1; 0 <= i; i--) {
      if (executive_biographies[i]["content"].indexOf("退任") === -1) {
        return executive_biographies[i]["content"];
      }
    }
  }
  return null;
}

export const generateRelocationMenu = () => {
  let year = moment().year();
  let month = moment().month();
  let relocationAtMenu = [];

  let relocationAtMonthsMenu = [
    { name: "1月", value: "01" },
    { name: "2月", value: "02" },
    { name: "3月", value: "03" },
    { name: "4月", value: "04" },
    { name: "5月", value: "05" },
    { name: "6月", value: "06" },
    { name: "7月", value: "07" },
    { name: "8月", value: "08" },
    { name: "9月", value: "09" },
    { name: "10月", value: "10" },
    { name: "11月", value: "11" },
    { name: "12月", value: "12" },
  ];

  const oldDataYear = 2020;
  // 2021年以降を対象とする
  const maxYearRanges = year - oldDataYear;
  for (let i = 0; i < maxYearRanges; i++) {
    let submenu = relocationAtMonthsMenu;

    if (i === 0) {
      submenu = submenu.filter((item) => {
        return parseInt(item.value, 10) <= month + 1;
      });
    }

    relocationAtMenu.push({
      name: year - i + "年",
      value: (year - i).toString(),
      submenu: submenu,
    });
  }

  relocationAtMenu.push({
    name: `${oldDataYear}年以前も表示する`,
    value: `before${oldDataYear}`,
    submenu: [],
  });

  return relocationAtMenu;
};

export const generateCapSizePerCorporationMenu = () => {
  let menu = [];
  const maxKeymanCount = 4;
  for (let i = 1; i <= maxKeymanCount; i++) {
    menu.push({
      name: "1企業につき" + i + "名まで",
      value: i.toString()
    });
  }

  return menu;
}

export const createValueToEntryMap = (menu) => {
  let valueToEntryMap = {};
  for (let i = 0; i < menu.length; i++) {
    if (menu[i].submenu.length === 0) {
      valueToEntryMap[menu[i].value] = { ...menu[i] };
    } else {
      for (let j = 0; j < menu[i].submenu.length; j++) {
        let entry = menu[i].submenu[j];
        valueToEntryMap[entry.value] = { ...entry, parent: menu[i].value, parent_name: menu[i].name };
      }
    }
  }
  return valueToEntryMap;
};

export const flattenMap = (map, menu, property = "value") => {
  let params = [];
  let keySeq = map.keySeq().toArray();

  keySeq.forEach((key) => {
    if (map.has(key) && map.get(key).count() > 0) {
      let submenu = map.get(key).toArray();

      let menuItem;
      if (menu) {
        menuItem = menu.find((m) => m.value === key);
      }

      if (menu && menuItem) {
        if (submenu.length === menuItem.submenu.length) {
          params.push(menuItem[property]);
        } else {
          menuItem.submenu.forEach((item) => {
            if (submenu.indexOf(item.value) > -1) {
              params.push(item[property]);
            }
          });
        }
      } else {
        submenu.forEach((item) => {
          params.push(item);
        });
      }
    } else {
      params.push(key);
    }
  });

  return params;
};

export const flattenNestedMenu = (nestedMenu) => {
  return nestedMenu.flatMap(menu => 
    menu.submenu.map(sub => ({
      ...sub,
      parentName: menu.name,
      parentValue: menu.value,
    }))
  );
}

export const isWeekday = (date) => {
  const sundayCount = 0;
  const saturdayCount = 6;
  if (date && date instanceof Date) {
    const day = date.getDay();
    return day !== sundayCount && day !== saturdayCount;
  } else if (date && date instanceof moment) {
    const day = date.day();
    return day !== sundayCount && day !== saturdayCount;
  }

  return false;
};

export const pad = (num, size) => {
  let s = num + "";
  while (s.length < size) s = "0" + s;
  return s;
};

export const stripDomain = (url) => {
  return url
    .replace("http://", "")
    .replace("https://", "")
    .replace("www.", "")
    .split(/[/?#]/)[0];
};

export const getQueryVariable = (q = "", variable = "", defaultValue = null) => {
  let query = q[0] === "?" ? q.substring(1) : q;
  let vars = query.split("&");

  let queryObj = {};

  for (let i in vars) {
    let pair = vars[i].split("=");

    const keyValueCount = 2;
    if (pair.length === keyValueCount) {
      let key = decodeURIComponent(pair[0]);
      let value = decodeURIComponent(pair[1]);

      if (Array.isArray(variable)) {
        if (variable.find((v) => v === key)) {
          queryObj[key] = value;
        }
      } else {
        queryObj[key] = value;
      }
    } else if (pair.length === 1) {
      let key = decodeURIComponent(pair[0]);
      queryObj[key] = defaultValue !== null ? defaultValue : "";
    }
  }

  if (typeof variable === "string") {
    return queryObj[variable];
  } else {
    return queryObj;
  }
};

export const formatErrorMessage = (error, prefix = "") => {
  let message = prefix;

  if (error && error.data && error.data.message) {
    let msg = error.data.message;

    if (typeof msg === "object") {
      if (Array.isArray(msg)) {
        message = message + " " + msg.join(" ");
      } else {
        message =
          message +
          " " +
          Object.values(msg)
            .map((item) => {
              if (Array.isArray(item)) {
                return item.join(" ");
              } else {
                return item;
              }
            })
            .join(" ");
      }
    } else if (typeof msg === "string") {
      message = message + " " + msg;
    }
  }

  return message;
};

export const importMappingToParams = (mapping, limit) => {
  const keys = mapping.keySeq();
  let params = {};

  keys.forEach((key) => {
    if (mapping.get(key)) {
      params[key] = mapping.get(key).toArray();
    }
  });

  if (limit !== undefined) {
    params["limit"] = limit;
  }

  params["crm_address_id"] = ["Id"];

  return params;
};

export const getLastUpdateText = (lastUpdate) => {
  if (!lastUpdate) {
    return "...";
  } else {
    if (moment(lastUpdate).add(1, "minutes").diff(moment()) > 0) {
      return "たった今";
    } else if (moment(lastUpdate).add(1, "hour").diff(moment()) > 0) {
      return moment().diff(moment(lastUpdate), "minutes") + "分前";
    } else {
      return moment(lastUpdate).format("YYYY年MM月DD日 HH:mm");
    }
  }
};

export const makeSalesforceMap = (map) => {
  let mapping = Map();

  salesforceImportMappingFormProps.forEach((prop) => {
    let set = Set();

    if (map && map.hasOwnProperty(prop.key) && Array.isArray(map[prop.key])) {
      map[prop.key].forEach((value) => {
        set = set.add(value);
      });
    }

    mapping = mapping.set(prop.key, set);
  });

  return mapping;
};

export const makeSalesforceExportMap = (map) => {
  let mapping = Map();

  Object.keys(map).forEach((sobject_name) => {
    let v = map[sobject_name];
    Object.keys(v).forEach((klKey) => {
      mapping = mapping.set(klKey, {
        sobject_name: sobject_name,
        field: v[klKey],
      });
    });
  });

  return mapping;
};

export const reverseSalesforceExeExportMap = (map) => {
  let keySeq = map.keySeq().toArray();
  let mapping = {};

  keySeq.forEach((key) => {
    let value = map.get(key);
    let sobject_name = value["sobject_name"];
    let field = value["field"];

    if (sobject_name && field) {
      if (mapping && mapping[sobject_name]) {
        mapping[sobject_name][key] = field;
      } else {
        mapping[sobject_name] = { [key]: field };
      }
    }
  });

  return mapping;
};

export const isOrderScheduledAfter = (orderScheduledAt) => {
  const today = new Date();
  const scheduled_at = new Date(orderScheduledAt);
  return orderScheduledAt && today < scheduled_at ? true : false
};

export const containsHtmlElement = (str) => {
  return /<\/?[a-z][\s\S]*>/i.test(str)
}

export const convertUrlToLink = (str) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  return str.replace(urlRegex, (url) => {
    return `<a href="${url}" target="_blank" style="color: #195AFF;">${url}</a>`;
  });
}

export const isEnabledUserRole = (user, scope, activity) => {
  if (user === null || user === undefined) {
    return false;
  }

  // 親アカウントであれば、true
  if (user.parent_id === null) {
    return true;
  }

  if (user.user_role === null || user.user_role[scope] === undefined) {
    return false;
  }

  return user.user_role[scope][activity] === true;
}

/**
 * ダウンロード履歴を閲覧できる権限があるか判定する
 * 親アカウントは常に可。子アカウントは download-history の read が必要
 *
 * @param {object} user ユーザー
 * @returns {boolean} 閲覧できる場合true
 */
export const canViewDownloadHistory = (user) => {
  return isEnabledUserRole(user, "download-history", "read");
}

export const difference = (object, base) => {
  return transform(object, (result, value, key) => {
    if (!isEqual(value, base[key])) {
      result[key] = isObject(value) && isObject(base[key]) ? difference(value, base[key]) : value;
    }
  });
}

export const isMinLetterOrderCount = (user, selectCount) => {
  // 最低注文数が設定されていない場合は、true
  if (user === null || user === undefined || user.min_letter_order_count === null) {
    return true;
  }

  // 最低注文数以上であれば、true
  return user.min_letter_order_count <= selectCount ? true : false;
}

/**
 * フィルター値をネストされたMap構造に変換する
 * @param {Array} values フィルター値の配列
 * @param {Object} config 設定オブジェクト（menu, map, hasParentMenuなどを含む）
 * @returns {Map} ネストされたMap構造
 */
export const convertFilterValuesToNestedMap = (values, config) => {
  let resultMap = Map();
  values.forEach(value => {
    let entry = config.map[value];
    if (entry) {
      if (entry.hasOwnProperty('parent')) {
        let submenu = resultMap.has(entry.parent) ? resultMap.get(entry.parent) : Set();
        resultMap = resultMap.set(entry.parent, submenu.add(value));
      } else {
        resultMap = resultMap.set(value, Set());
      }
    }

    // 親エントリーの処理
    if (config && config.hasParentMenu) {
      let parentEntry = config.menu.find(item => item.value === value);
      if (parentEntry) {
        let submenu = Set();
        parentEntry.submenu.forEach(item => {
          submenu = submenu.add(item.value);
        });
        resultMap = resultMap.set(value, submenu);
      }
    }
  });
  return resultMap;
};



// WEBPACK FOOTER //
// ./src/utils/utils.js